import { nextTick, ref, type Ref } from "vue";
import { OperationInput, CognitoUserCommonInput } from "@/generated/graphql";
import { localStorageGetCustomerCode, localStorageGetUserId } from "@/common/localStorageUtil";
import { deleteCognitoUser } from "@/common/graphql/cognito";
import type { ListData } from "../types";

/**
 * 削除処理と関連する状態更新を提供する composable。
 *
 * @returns 画面ロジックで利用する状態・関数
 */
export const useOperatorListDelete = (
  editPage: Ref<boolean>,
  confirmPage: Ref<boolean>,
  confirmDeleteDialog: Ref<boolean>,
  overlay: Ref<boolean>,
  errdialog: Ref<boolean>,
  errMessage: Ref<string>,
  selectListCheckbox: Ref<ListData[]>,
  searchQuery: () => Promise<void>,
) => {
  const deleteItem = ref<ListData | null>(null);

  /**
   * オペレータ削除アイコン @Click
   */
  function onClickDelete(item: ListData): void {
    deleteItem.value = item;
    confirmPage.value = true;
  }

  /**
   * 削除確認キャンセル @Click
   */
  function onClickConfirmDeleteCancel(): void {
    editPage.value = false;
    confirmPage.value = false;
  }

  /**
   * 削除確認OK @Click
   */
  async function onClickDeleteComplete(): Promise<void> {
    overlay.value = true;
    if (!deleteItem.value) {
      return;
    }
    try {
      const input: CognitoUserCommonInput = {
        customer_code: localStorageGetCustomerCode(),
        chain_code: deleteItem.value.chainCode,
        user_id: deleteItem.value.id,
      };
      const operationInput: OperationInput = {
        area_code: "10",
        detail: "操作詳細",
        display_category: "オペレータ一覧",
        operation: "オペレータ削除",
        user_id: localStorageGetUserId(),
      };
      await deleteCognitoUser(input, operationInput);
      nextTick(async () => {
        await searchQuery();
      });
    } catch (err) {
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "不明なエラーが発生しました。";
        console.error("未知のエラー:", err);
      }
      return;
    } finally {
      editPage.value = false;
      confirmPage.value = false;
      overlay.value = false;
    }
  }

  /**
   * 一括削除 @Click
   */
  function onClickCheckDelete(): void {
    if (selectListCheckbox.value.length === 0) {
      return;
    }
    confirmDeleteDialog.value = true;
  }

  /**
   * 一括削除 実行 @Click
   */
  async function onClickCheckDeleteComplete(): Promise<void> {
    try {
      overlay.value = true;
      const operationInput: OperationInput = {
        area_code: "10",
        detail: "操作詳細",
        display_category: "オペレータ一覧",
        operation: "オペレータ削除",
        user_id: localStorageGetUserId(),
      };
      for (const val of selectListCheckbox.value) {
        const input: CognitoUserCommonInput = {
          customer_code: localStorageGetCustomerCode(),
          chain_code: val.chainCode,
          user_id: val.id,
        };
        await deleteCognitoUser(input, operationInput);
      }
      nextTick(async () => {
        await searchQuery();
      });
    } catch (err) {
      errdialog.value = true;
      if (err instanceof Error) {
        errMessage.value = err.message;
      } else {
        errMessage.value = "不明なエラーが発生しました。";
        console.error("未知のエラー:", err);
      }
      return;
    } finally {
      confirmDeleteDialog.value = false;
      overlay.value = false;
    }
  }

  return {
    deleteItem,
    onClickDelete,
    onClickConfirmDeleteCancel,
    onClickDeleteComplete,
    onClickCheckDelete,
    onClickCheckDeleteComplete,
  };
};
